import { Link, useNavigate } from 'react-router-dom'
import IconBox from '../../components/common/icon-box'
import MainNav from '../../components/homepage/main-nav'
import Layout from '../../components/layout/layout'
import { useEffect, useMemo, useState } from 'react'
import { useUser } from '../../common/hooks/auth/useUser'
import { useMutation } from '@tanstack/react-query'
import InfoMessage from '../../components/common/info-message'
import { useTranslation } from '../../useTranslation'
import Button from '../../components/common/button'
import { useIamApi } from '../../common/api-context'
import { ErrorResponse } from '../../common/helpers/apiHelper'
import { toast } from 'react-toastify'
import Loader from '../../components/common/Loader'

const ConfirmEmail = () => {
    const navigate = useNavigate()
    const { isAuthenticated, extractTokenFromUrl } = useUser()
    const iamApi = useIamApi()
    const [expired, setTokenExpired] = useState(false)

    // Redirect if authenticated
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/')
        }
    }, [isAuthenticated, navigate])

    const { t } = useTranslation()

    const confirmEmail = useMutation({
        mutationFn: async (token: string) => {
            return iamApi.confirmEmail({ token })
        },
        onError: async (error: ErrorResponse) => {
            if (error.response.status === 422 && error.response.data.properties.errorCode === 'error.token.expired') {
                setTokenExpired(true)
            }
        },
    })

    const resendVerificationEmailForAttemptedToken = useMutation({
        mutationFn: async () => {
            await iamApi.resendEmailVerificationByOldToken({ token: extractTokenFromUrl()! })
        },
        onSuccess: () => {
            toast.success(t('login.verificationEmailSuccessfullySent'))
            navigate('/')
        },
    })

    useEffect(() => {
        confirmEmail.mutate(extractTokenFromUrl()!)
    }, [])

    const displayMode = useMemo(() => {
        if (confirmEmail.isPending) return 'loading'
        else if (expired) return 'expired'
        else if (confirmEmail.isSuccess || confirmEmail.isError) return 'confirmStatus'
    }, [confirmEmail.isPending, expired])

    return (
        <Layout>
            <MainNav />
            <div className="mx-auto my-16 w-3/6 max-tablet:w-2/3">
                {displayMode === 'loading' && (
                    <div className="flex items-center justify-center">
                        <Loader></Loader>
                    </div>
                )}
                {displayMode === 'expired' && (
                    <IconBox icon="circle-exclamation">
                        <div className="py-10 px-2">
                            <p className="font-bold text-xl text-left mb-3 leading-0 max-tablet:text-base">{t('verificationLinkExpired')}</p>

                            <div className="text-left">{t('verificationLinkExpiredDescription')}</div>
                            <div className="mt-3">
                                <Button
                                    type="squareBlue"
                                    icon="envelope"
                                    text={t('login.resendVerificationEmail')}
                                    onClick={() => resendVerificationEmailForAttemptedToken.mutate()}
                                    isLoading={resendVerificationEmailForAttemptedToken.isPending}
                                />
                            </div>
                        </div>
                    </IconBox>
                )}
                {displayMode === 'confirmStatus' && (
                    <IconBox icon="address-card">
                        <div className="py-10 px-2">
                            {confirmEmail.isSuccess ? (
                                <InfoMessage type="success" label={t('login.emailConfirmSuccess')} icon="check" />
                            ) : (
                                <InfoMessage type="error" label={t('login.emailConfirmError')} icon="xmark" />
                            )}
                            <div className="text-center -mb-4 flex gap-4 justify-center">
                                <Link to="/">
                                    <Button text={t('backToRingo')} type="squareYellow" />
                                </Link>
                                {confirmEmail.isSuccess && (
                                    <Link to="/auth/login">
                                        <Button text={t('login')} type="squareBlue" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </IconBox>
                )}
            </div>
        </Layout>
    )
}

export default ConfirmEmail
